import { useEffect, useRef, useState } from 'react'
import { Outlet } from 'react-router-dom'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  buildTenantHostname,
  clearTenantHandoffStarted,
  getPlatformAdminSubdomain,
  isTenantHandoffBounceLoop,
  readPostLogoutSkipAuthApexRedirectFlag,
  redirectToSubdomainHandoff,
  rememberLastTenantSubdomain,
  setTenantHandoffFailedFlag,
} from '@/features/auth/lib'
import { isPostInviteWelcomePending } from '@/features/dashboard/lib/post-invite-welcome'
import { logger } from '@/lib/logger'

/**
 * When a tenant user opens the auth apex (`app.localhost:3000` / `app.{root}`) or a wrong
 * tenant label — or a Platform Admin is not on `admin.*` — redirect to the
 * correct hostname using the post-login handoff flow.
 */
export function TenantHostRedirect() {
  const user = useSessionStore((s) => s.user)
  const explicitLogout = useSessionStore((s) => s.explicitLogout)
  const postLogoutPending = readPostLogoutSkipAuthApexRedirectFlag()
  const startedRef = useRef(false)
  const [handoffFailed, setHandoffFailed] = useState(false)

  const isPlatformAdmin = user?.role === 'PLATFORM_ADMIN'
  const hostSub = isPlatformAdmin
    ? getPlatformAdminSubdomain()
    : (user?.subdomain?.trim() ?? '')
  const hostname =
    globalThis.window === undefined ? '' : globalThis.location.hostname
  const targetHost =
    hostSub.length > 0 ? buildTenantHostname(hostname, hostSub) : hostname
  const needsRedirect = hostSub.length > 0 && targetHost !== hostname
  const blocked = explicitLogout || postLogoutPending

  useEffect(() => {
    if (blocked || !needsRedirect || handoffFailed || startedRef.current) {
      return
    }

    // Apex↔tenant bounce: we already tried handoff and landed back on apex.
    if (isTenantHandoffBounceLoop()) {
      startedRef.current = true
      setHandoffFailed(true)
      setTenantHandoffFailedFlag()
      clearTenantHandoffStarted()
      logger.error('Tenant host handoff bounce loop detected — staying on apex')
      return
    }

    startedRef.current = true

    void (async () => {
      try {
        const postInviteWelcome = isPostInviteWelcomePending(
          new URLSearchParams(globalThis.location.search),
        )
        // Apex localStorage hint for cold tabs (host-only cookies ≠ shared).
        rememberLastTenantSubdomain(hostSub)
        const handedOff = await redirectToSubdomainHandoff(hostSub, {
          postInviteWelcome,
        })
        if (!handedOff) {
          startedRef.current = false
          setHandoffFailed(true)
          setTenantHandoffFailedFlag()
        }
      } catch (err) {
        startedRef.current = false
        setHandoffFailed(true)
        setTenantHandoffFailedFlag()
        logger.error('Tenant host handoff failed', { err })
      }
    })()
  }, [blocked, needsRedirect, hostSub, handoffFailed])

  useEffect(() => {
    if (!needsRedirect) {
      clearTenantHandoffStarted()
    }
  }, [needsRedirect])

  if (blocked || !hostSub) {
    return <Outlet />
  }

  if (!needsRedirect || handoffFailed) {
    return <Outlet />
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <CircularProgress />
    </Box>
  )
}
